import type {LocationQuery} from 'vue-router'
import type {LandingComparison} from '@/components/www/landing/landingComparison'
import type {LandingFaqItem} from '@/components/www/landing/landingFaq'
import {hrefWithCampaignParams} from '@/lib/campaignParams'
import {formatPrice, PLANS} from '@/components/www/pricing/pricingData'

export const R2_PATH = '/marketplace-listing-alternatives-r2'
export const R2_DEMO_PATH = '/demo-request-r2'

export const R2_SOURCE = 'marketplace-listing-alternatives-r2'

/**
 * The shared booking page says the walkthrough is 30 minutes, and the Calendly
 * event URL does not record a length. Landing buttons keep the approved
 * "20-min" label. This page does not repeat that length.
 */
export const R2_BOOKING_MINUTES = 30

// www quotes 1-year contract prices only (pricingData ADVERTISED_BILLING_TERM).
const startingPlan = [...PLANS].sort((a, b) => a.contractMonthlyPrice - b.contractMonthlyPrice)[0]

export const R2_STARTING_PLAN = startingPlan

export function r2DemoHref(cta: string, query: LocationQuery = {}): string {
    return hrefWithCampaignParams(R2_DEMO_PATH, query, {
        source: R2_SOURCE,
        variant: 'r2',
        cta,
    })
}

export const R2_HERO_REASSURANCE = 'See the listing workflow, review your current setup, and discuss what switching would involve.'

export const R2_DEMO_ANSWER = 'We\'ll review your current setup, walk through a relevant listing example, and explain the migration steps and pricing for your catalog.'

export const R2_NOLI_QUOTE = 'I don\'t think we could ever go back to manually doing those spreadsheets, honestly.'

export const R2_NOLI_ATTRIBUTION = 'Danielle Devane, COO, The Noli Shop'

export const R2_CARD_COPY = {
    List: {
        title: 'No more spreadsheets',
        titleLine2: '',
        body: 'Osello reads styles, colorways, and sizes from Shopify. No export, and no mapping project.',
    },
    Fix: {
        title: 'Resolve rejections with less manual work.',
        titleLine2: '',
        body: 'Osello prepares a fix from the rejection. Your team reviews what is flagged. Some rejections still need the retailer.',
    },
    Grow: {
        title: 'Add a marketplace.',
        titleLine2: 'Keep the collection.',
        body: 'The same Shopify catalog, formatted for that retailer. A new marketplace still needs the retailer\'s approval.',
    },
} as const

export const R2_NOLI_STATS = [
    {value: 'Before Osello', label: 'up to 6 weeks per launch.'},
    {value: '0', label: 'Catalog uploads still done by hand'},
]

export const R2_COMPARISON: LandingComparison = {
    eyebrow: 'Compared with',
    title: 'Where the work sits.',
    intro: 'Tools and services differ. These rows describe the usual pattern, not every product on the market.',
    columns: [
        {id: 'feed', name: 'Feed management tools'},
        {id: 'agency', name: 'Agency/manual service'},
        {id: 'osello', name: 'Osello', isUs: true, wordmark: true},
    ],
    rows: [
        {
            id: 'edit',
            label: 'Editing an individual listing',
            values: {
                feed: 'Often a shared rule. A change can apply to more than one listing.',
                agency: 'Someone updates that listing in their sheet or in the retailer account.',
                osello: 'Each listing is a stored record you can open and edit.',
            },
        },
        {
            id: 'rejections',
            label: 'Rejection handling',
            values: {
                feed: 'Depends on the tool. Many apply one rule across the catalog.',
                agency: 'You send the rejection. They key the fix on their timeline.',
                osello: 'Osello prepares a fix. Your team reviews flagged items. Some still need the retailer.',
            },
        },
        {
            id: 'variants',
            label: 'Sizes and colorways',
            values: {
                feed: 'Variants are usually rows you map. How colorways group depends on the tool.',
                agency: 'Handled by hand, each drop.',
                osello: 'One style, its sizes, and its colorways, merged from Shopify into that retailer\'s listing.',
            },
        },
        {
            id: 'ongoing',
            label: 'Who handles ongoing work',
            values: {
                feed: 'You build the rules, then you maintain them.',
                agency: 'They do the updates, on their timeline.',
                osello: 'Osello prepares the listing. Your team reviews flagged items and approves submission.',
            },
        },
        {
            id: 'commercial',
            label: 'Commercial model',
            values: {
                feed: 'Usually a flat monthly fee, often tiered by catalog size.',
                agency: 'If your current provider charges a percentage of sales, compare that with Osello\'s flat monthly pricing.',
                osello: 'Flat monthly fee. Osello charges no sales commission.',
            },
        },
    ],
}

export const R2_FAQ: readonly LandingFaqItem[] = [
    {
        id: 'demo',
        question: 'What happens in the demo?',
        answer: R2_DEMO_ANSWER,
    },
    {
        id: 'cost',
        question: 'What does it cost?',
        answer: `Plans from ${formatPrice(startingPlan.contractMonthlyPrice)}/month. Osello charges no sales commission. The ${startingPlan.name} plan includes up to ${startingPlan.limits.activeSkus.toLocaleString('en-US')} Active SKUs, ${formatPrice(startingPlan.limits.monthlyGmv)} in monthly GMV, and ${startingPlan.limits.maxMarketplaces} marketplaces. Catalog size and marketplace count can change the plan. The demo covers the price for your setup.`,
    },
    {
        id: 'current-tool',
        question: 'Do we have to turn off our current tool first?',
        answer: 'No. We review the current setup and build the mapping while your current connection stays in place. You approve the cutover. We do not switch the connection before that.',
    },
    {
        id: 'live',
        question: 'What happens to live listings and orders?',
        answer: 'Existing seller accounts stay. Orders keep their path into Shopify through the connection you are already using until you approve the change. Additional marketplaces remain subject to retailer approval.',
    },
    {
        id: 'already-selling',
        question: 'Is this for brands already selling?',
        answer: 'Yes. The page is for brands already live on a marketplace, including Nordstrom, Macy\'s, and Target+, who want the catalog work off the spreadsheet.',
    },
    {
        id: 'shopify',
        question: 'Is Shopify required?',
        answer: 'The listing workflow on this page starts from a Shopify catalog. The demo does not ask you to connect Shopify, grant access, or upload a catalog.',
    },
]
